import React, { useMemo, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import apiFetch from '../../../utils/apiFetch';
import spellCatalog from '../../../data/spells';
import { SKILLS } from '../skillSchema';
import { getSkillTraining } from '../../../shared/rules/abilityChecks.mjs';
import { getClassLevel } from '../../../shared/rules/classIdentity.mjs';
import { BONUS_PROFICIENCY_COUNT, canChangeMagicalDiscoveries, getBonusProficiencies, getMagicalDiscoveries, isDiscoverySpell, magicalDiscoveriesProblem } from '../../../shared/rules/subclasses/bard/lore.mjs';

const put = async (characterId, route, body) => {
  const response = await apiFetch(`/characters/${encodeURIComponent(characterId)}/classes/bard/${route}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const result = await response.json();
  if (!response.ok) throw new Error(result.message || 'Could not save.');
  return result;
};
const mergeBard = (onCharacterChange, changes, occupation) => onCharacterChange?.((previous) => ({ ...previous, ...(occupation ? { occupation } : {}),
  classState: { ...previous.classState, bard: { ...previous.classState?.bard, ...changes } } }));
const byLevel = (a, b) => Number(a.level) - Number(b.level) || String(a.name).localeCompare(String(b.name));

/** Bonus Proficiencies (College of Lore 3): three skills, chosen once. */
export function BonusProficienciesChooser({ form, characterId, onCharacterChange }) {
  const saved = getBonusProficiencies(form);
  const [picks, setPicks] = useState([]);
  const [saving, setSaving] = useState(false), [error, setError] = useState('');
  const label = (key) => SKILLS.find((skill) => skill.key === key)?.label || key;
  if (saved.length) return <div className="fighting-style-chooser"><p className="feature-sheet-hint">{saved.map(label).join(', ')}</p></div>;
  const open = SKILLS.filter(({ key }) => !getSkillTraining(form, key).proficient);
  const toggle = (key) => setPicks((previous) => (previous.includes(key) ? previous.filter((pick) => pick !== key) : previous.length < BONUS_PROFICIENCY_COUNT ? [...previous, key] : previous));
  const choose = async () => {
    setSaving(true); setError('');
    try {
      const result = await put(characterId, 'bonus-proficiencies', { skills: picks });
      mergeBard(onCharacterChange, { bonusProficiencies: result.bonusProficiencies }, result.occupation);
    } catch (failure) { setError(failure.message); } finally { setSaving(false); }
  };
  return <div className="fighting-style-chooser">
    <p className="feature-sheet-hint">Choose three skills you aren’t proficient in. This choice is permanent. {picks.length} of {BONUS_PROFICIENCY_COUNT} chosen.</p>
    {error && <Alert variant="danger">{error}</Alert>}
    {open.map(({ key }) => <Form.Check key={key} id={`lore-skill-${key}`} className="small" label={label(key)} checked={picks.includes(key)} disabled={saving || (!picks.includes(key) && picks.length >= BONUS_PROFICIENCY_COUNT)} onChange={() => toggle(key)}/>)}
    <div className="fighting-style-actions"><Button size="sm" onClick={choose} disabled={saving || picks.length !== BONUS_PROFICIENCY_COUNT}>{saving ? 'Saving…' : 'Save Bonus Proficiencies'}</Button></div>
  </div>;
}

/** Magical Discoveries (College of Lore 6): two Cleric, Druid or Wizard spells; one can change each Bard level. */
export function MagicalDiscoveriesChooser({ form, characterId, onCharacterChange }) {
  const saved = getMagicalDiscoveries(form);
  const [picks, setPicks] = useState([saved[0] || '', saved[1] || '']);
  const [saving, setSaving] = useState(false), [error, setError] = useState('');
  const level = getClassLevel(form, 'bard');
  const offered = useMemo(() => Object.entries(spellCatalog).map(([spellId, spell]) => ({ ...spell, spellId })).filter((spell) => isDiscoverySpell(spell, level)).sort(byLevel), [level]);
  const locked = !canChangeMagicalDiscoveries(form);
  const problem = magicalDiscoveriesProblem(form, picks, spellCatalog);
  const unchanged = saved.length && saved.every((id, index) => picks[index] === id);
  const choose = async () => {
    setSaving(true); setError('');
    try { mergeBard(onCharacterChange, { magicalDiscoveries: (await put(characterId, 'magical-discoveries', { spells: picks })).magicalDiscoveries }); }
    catch (failure) { setError(failure.message); } finally { setSaving(false); }
  };
  return <div className="fighting-style-chooser">
    <p className="feature-sheet-hint">{locked ? 'You can replace one of these when you next gain a Bard level.' : saved.length ? 'You can replace one of these now.' : 'Each is always prepared, on top of your prepared spells.'}</p>
    {error && <Alert variant="danger">{error}</Alert>}
    {[0, 1].map((slot) => <div key={slot}>
      <Form.Label htmlFor={`lore-discovery-${slot}`}>Spell {slot + 1}</Form.Label>
      <Form.Select id={`lore-discovery-${slot}`} value={picks[slot]} disabled={saving || locked} onChange={(event) => setPicks((previous) => previous.map((pick, index) => (index === slot ? event.target.value : pick)))}>
        <option value="">Choose a spell</option>
        {offered.map((spell) => <option key={spell.spellId} value={spell.spellId}>{Number(spell.level) ? `Level ${spell.level}` : 'Cantrip'} · {spell.name}</option>)}
      </Form.Select>
    </div>)}
    <div className="fighting-style-actions"><Button size="sm" onClick={choose} disabled={saving || locked || Boolean(problem) || Boolean(unchanged)} title={problem}>{saving ? 'Saving…' : 'Save Magical Discoveries'}</Button></div>
  </div>;
}
